import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickActionPanel = ({ favoriteCharities, recurringDonations }) => {
  const [activeTab, setActiveTab] = useState('favorites');
  const navigate = useNavigate();

  const handleDonateToFavorite = (charityId) => {
    navigate(`/charity-profile?id=${charityId}`);
  };

  const handleManageRecurring = (donationId) => {
    console.log('Managing recurring donation:', donationId);
  };

  const handleGenerateTaxDoc = () => {
    console.log('Generating tax documentation');
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <h3 className="text-lg font-semibold text-card-foreground">Quick Actions</h3>
        <p className="text-sm text-muted-foreground">Manage your giving activities</p>
      </div>
      {/* Tab Navigation */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setActiveTab('favorites')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-micro ${
            activeTab === 'favorites' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <Icon name="Heart" size={16} />
            <span>Favorites</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('recurring')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-micro ${
            activeTab === 'recurring' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <Icon name="RefreshCw" size={16} />
            <span>Recurring</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('tools')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-micro ${
            activeTab === 'tools' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <Icon name="Settings" size={16} />
            <span>Tools</span>
          </div>
        </button>
      </div>
      <div className="p-6">
        {/* Favorite Charities Tab */}
        {activeTab === 'favorites' && (
          <div className="space-y-4">
            {favoriteCharities?.length > 0 ? (
              favoriteCharities?.map((charity) => (
                <div key={charity?.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Heart" size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-card-foreground text-sm">{charity?.name}</p>
                      <p className="text-xs text-muted-foreground">{charity?.category}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDonateToFavorite(charity?.id)}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Donate
                  </Button>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Icon name="Heart" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No favorite charities yet</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/cause-discovery')}
                  className="mt-3"
                >
                  Discover Charities
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Recurring Donations Tab */}
        {activeTab === 'recurring' && (
          <div className="space-y-4">
            {recurringDonations?.length > 0 ? (
              recurringDonations?.map((donation) => (
                <div key={donation?.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                      <Icon name="RefreshCw" size={16} className="text-secondary" />
                    </div>
                    <div>
                      <p className="font-medium text-card-foreground text-sm">{donation?.charity}</p>
                      <p className="text-xs text-muted-foreground">
                        ${donation?.amount} • {donation?.frequency}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      donation?.status === 'active' ?'bg-success/10 text-success' :'bg-warning/10 text-warning'
                    }`}>
                      {donation?.status}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleManageRecurring(donation?.id)}
                      iconName="Settings"
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Icon name="RefreshCw" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No recurring donations set up</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/cause-discovery')}
                  className="mt-3"
                >
                  Set Up Recurring
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Tools Tab */}
        {activeTab === 'tools' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Button
                variant="outline"
                onClick={handleGenerateTaxDoc}
                iconName="FileText"
                iconPosition="left"
                className="justify-start h-auto p-4"
              >
                <div className="text-left">
                  <div className="font-medium">Tax Documents</div>
                  <div className="text-xs text-muted-foreground">Generate yearly summary</div>
                </div>
              </Button>
              
              <Button
                variant="outline"
                onClick={() => navigate('/giving-challenges')}
                iconName="Trophy"
                iconPosition="left"
                className="justify-start h-auto p-4"
              >
                <div className="text-left">
                  <div className="font-medium">Challenges</div>
                  <div className="text-xs text-muted-foreground">Join giving campaigns</div>
                </div>
              </Button>
              
              <Button
                variant="outline"
                onClick={() => navigate('/community-feed')}
                iconName="Users"
                iconPosition="left"
                className="justify-start h-auto p-4"
              >
                <div className="text-left">
                  <div className="font-medium">Community</div>
                  <div className="text-xs text-muted-foreground">Connect with donors</div>
                </div>
              </Button>
              
              <Button
                variant="outline"
                onClick={() => navigate('/cause-discovery')}
                iconName="Search"
                iconPosition="left"
                className="justify-start h-auto p-4"
              >
                <div className="text-left">
                  <div className="font-medium">Discover</div>
                  <div className="text-xs text-muted-foreground">Find new causes</div>
                </div>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickActionPanel;